import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Users, FileText, Clock, CalendarDays, Sparkles } from 'lucide-react';
import AddApprenantDialog from '../../components/AddApprenantDialog';
import AddActionDialog from '../../components/AddActionDialog';
import ApprenantCard from '../../components/ApprenantCard';
import FreinsDetaillesModule from '../../components/FreinsDetaillesModule';
import DocumentUploader from '../../components/DocumentUploader';
import EntreeDispositifCard from '../../components/EntreeDispositifCard';
import ComptesRendusBeneficiaire from '../../components/ComptesRendusBeneficiaire';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const calcDureeMinutes = (item) => {
  if (item.duree_minutes) return item.duree_minutes;
  if (item.heure_debut && item.heure_fin) {
    const [hd, md] = item.heure_debut.split(':').map(Number);
    const [hf, mf] = item.heure_fin.split(':').map(Number);
    const diff = (hf * 60 + mf) - (hd * 60 + md);
    return diff > 0 ? diff : 0;
  }
  return 0;
};

const fmtDate = (date) => {
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch { return date; }
};

// ─── Sous-composant : item unifié (RDV / Atelier / Événement) ───────────────
const TimelineItem = ({ item }) => {
  const styles = {
    rdv: {
      bg: 'bg-blue-50', border: 'border-blue-200',
      badge: 'bg-blue-100 text-blue-700', label: 'RDV individuel'
    },
    atelier: {
      bg: 'bg-emerald-50', border: 'border-emerald-200',
      badge: 'bg-emerald-100 text-emerald-700', label: 'Atelier'
    },
    evenement: {
      bg: 'bg-purple-50', border: 'border-purple-200',
      badge: 'bg-purple-100 text-purple-700', label: 'Événement collectif'
    },
  };
  const s = styles[item.kind] || styles.rdv;

  const presence = item.statut_presence;
  const presenceLabel =
    presence === 'present' ? 'Présent' :
    presence === 'absent_justifie' ? 'Abs. justifiée' :
    presence === 'absent' ? 'Absent' :
    'Non défini';
  const presenceColor =
    presence === 'present' ? 'bg-emerald-100 text-emerald-700' :
    presence === 'absent_justifie' ? 'bg-amber-100 text-amber-700' :
    presence === 'absent' ? 'bg-red-100 text-red-700' :
    'bg-slate-100 text-slate-600';

  return (
    <div className={`p-4 rounded-xl border ${s.bg} ${s.border}`} data-testid={`timeline-${item.kind}-${item.id}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${s.badge} text-xs`}>{s.label}</Badge>
            <span className="font-bold text-slate-900 truncate">{item.titre}</span>
          </div>
          <div className="text-sm text-slate-600 mt-1">
            {fmtDate(item.date)}
            {item.heure_debut && ` • ${item.heure_debut}${item.heure_fin ? ' – ' + item.heure_fin : ''}`}
          </div>
          {item.type_label && (
            <div className="text-xs text-slate-500 mt-1">Type : {item.type_label}</div>
          )}
          {item.notes && (
            <div className="text-sm text-slate-500 mt-2 line-clamp-2">{item.notes}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className={`px-3 py-1 rounded-full text-sm font-bold ${s.badge}`}>
            {item.duree} min
          </div>
          <div className={`px-2 py-0.5 rounded-full text-xs font-bold ${presenceColor}`}>
            {presenceLabel}
          </div>
          {item.signed && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
              ✓ Signé
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Composant principal ────────────────────────────────────────────────────
const AnimateurBeneficiairesView = ({
  beneficiaires,
  selectedBeneficiaire,
  setSelectedBeneficiaire,
  beneficiaireActions = [],
  ateliers = [],
  activites = [],
  onGeneratePDF,
  onLoadBeneficiaireData,
  onRefresh
}) => {

  // ── Construire la timeline unifiée pour le bénéficiaire sélectionné ─────
  const timeline = useMemo(() => {
    if (!selectedBeneficiaire) return [];
    const benefId = selectedBeneficiaire.id;
    const items = [];

    // 1. Actions / RDV individuels
    beneficiaireActions.forEach(a => {
      items.push({
        id: a.id,
        kind: 'rdv',
        titre: a.titre || a.type_action || 'Rendez-vous',
        date: a.date,
        heure_debut: a.heure_debut,
        heure_fin: a.heure_fin,
        type_label: a.type_action,
        notes: a.notes || a.description,
        duree: calcDureeMinutes(a),
        statut_presence: a.statut_presence,
        signed: Boolean(a.signature_apprenant && a.signature_intervenant),
      });
    });

    // 2. Ateliers où le bénéficiaire est participant
    (ateliers || []).forEach(at => {
      const participant = at.participants?.find(
        p => p.id === benefId || p.apprenant_id === benefId
      );
      if (!participant) return;
      items.push({
        id: at.id,
        kind: 'atelier',
        titre: at.titre || 'Atelier',
        date: at.date,
        heure_debut: at.heure_debut,
        heure_fin: at.heure_fin,
        type_label: at.type_atelier,
        notes: at.notes || at.description,
        duree: calcDureeMinutes(at),
        statut_presence: participant.statut_presence
          || (participant.present === true ? 'present' : participant.present === false ? 'absent' : 'non_defini'),
        signed: Boolean(participant.signature),
      });
    });

    // 3. Activités intervenant collectives rattachées à ce bénéficiaire
    (activites || []).forEach(act => {
      const ids = act.beneficiaires_ids || [];
      if (!ids.includes(benefId)) return;
      // On affiche en "événement collectif" si > 1 bénéficiaire rattaché
      const isCollective = ids.length > 1;
      items.push({
        id: act.id,
        kind: isCollective ? 'evenement' : 'rdv',
        titre: act.titre || 'Activité',
        date: act.date,
        heure_debut: act.heure_debut,
        heure_fin: act.heure_fin,
        type_label: act.type,
        notes: act.notes,
        duree: Math.round(calcDureeMinutes(act) / Math.max(ids.length, 1)),
        statut_presence: 'present',
        signed: false,
      });
    });

    // Tri décroissant par date
    items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return items;
  }, [selectedBeneficiaire, beneficiaireActions, ateliers, activites]);

  // Total minutes
  const totalMinutes = timeline.reduce((s, i) => s + (i.duree || 0), 0);
  const totalHeures  = Math.round(totalMinutes / 60 * 10) / 10;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Liste des bénéficiaires */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Bénéficiaires
              <span className="text-sm font-normal text-slate-500 ml-2">({beneficiaires.length})</span>
            </CardTitle>
            <AddApprenantDialog onSuccess={onRefresh} />
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {beneficiaires.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucun bénéficiaire</p>
              ) : (
                beneficiaires.map((beneficiaire) => {
                  const statut = beneficiaire.statut;
                  const isActif = statut === 'actif';
                  const isEnAttente = statut === 'en_attente_consentement';
                  const isRefus = statut === 'refus_dispositif';
                  return (
                    <div
                      key={beneficiaire.id}
                      data-testid={`beneficiaire-item-${beneficiaire.id}`}
                      onClick={() => setSelectedBeneficiaire(beneficiaire)}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        selectedBeneficiaire?.id === beneficiaire.id
                          ? 'bg-blue-50 border-2 border-blue-200'
                          : 'bg-slate-50 border-2 border-transparent hover:border-blue-100'
                      }`}
                    >
                      <div className="font-bold text-slate-900">
                        {beneficiaire.prenom} {beneficiaire.nom}
                      </div>
                      <div className="text-sm text-slate-500">{beneficiaire.zone_geographique}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {isActif && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Actif</Badge>}
                        {isEnAttente && <Badge className="bg-amber-100 text-amber-700 text-xs">En attente consentement</Badge>}
                        {isRefus && <Badge className="bg-red-100 text-red-700 text-xs">A refusé</Badge>}
                        {beneficiaire.actif === false && (
                          <Badge className="bg-slate-100 text-slate-600 text-xs">Sorti</Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Détails du bénéficiaire sélectionné */}
      <div className="lg:col-span-2">
        {selectedBeneficiaire ? (
          <div className="space-y-6">
            <ApprenantCard
              bénéficiaire={selectedBeneficiaire}
              onUpdate={onRefresh}
              userRole="animateur"
            />

            {/* Carte consentement / entrée dans le dispositif */}
            <EntreeDispositifCard
              bénéficiaireId={selectedBeneficiaire.id}
              bénéficiaireEmail={selectedBeneficiaire.email}
              bénéficiaireNom={`${selectedBeneficiaire.prenom} ${selectedBeneficiaire.nom}`}
              onValidated={onRefresh}
            />

            <FreinsDetaillesModule bénéficiaireId={selectedBeneficiaire.id} />

            <DocumentUploader bénéficiaireId={selectedBeneficiaire.id} />

            {/* Outil de suivi : comptes-rendus de RDV */}
            <ComptesRendusBeneficiaire
              beneficiaireId={selectedBeneficiaire.id}
              beneficiaireNom={`${selectedBeneficiaire.prenom} ${selectedBeneficiaire.nom}`}
            />

            {/* Section Actions, RDV, Ateliers & Événements */}
            <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle
                  className="text-xl font-bold"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  Actions, RDV, Ateliers & Événements
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    onClick={onGeneratePDF}
                    data-testid="generate-pdf-button"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Générer PDF
                  </Button>
                  <AddActionDialog
                    bénéficiaireId={selectedBeneficiaire.id}
                    onSuccess={() => onLoadBeneficiaireData(selectedBeneficiaire.id)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {/* Compteur heures global */}
                <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-lg font-bold text-blue-900">
                        Total : {totalHeures} h
                      </div>
                      <div className="text-sm text-blue-700 flex flex-wrap gap-3 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {timeline.filter(i => i.kind === 'rdv').length} RDV
                        </span>
                        <span className="flex items-center gap-1 text-emerald-700">
                          <CalendarDays className="w-3.5 h-3.5" />
                          {timeline.filter(i => i.kind === 'atelier').length} ateliers
                        </span>
                        <span className="flex items-center gap-1 text-purple-700">
                          <Sparkles className="w-3.5 h-3.5" />
                          {timeline.filter(i => i.kind === 'evenement').length} événements
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline unifiée */}
                {timeline.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500 text-sm">
                      Aucune action, aucun RDV ni atelier enregistré pour ce bénéficiaire.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((item) => (
                      <TimelineItem key={`${item.kind}-${item.id}`} item={item} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="bg-white border-slate-200 rounded-3xl p-12 shadow-sm">
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-bold text-slate-400 mb-2">Sélectionnez un bénéficiaire</h3>
              <p className="text-slate-400">Cliquez sur un bénéficiaire dans la liste pour voir ses détails</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AnimateurBeneficiairesView;
